/**
 * Open sub-account — master creates sub with drill-down tree placement (左/右区).
 * From 全球玩家(表): locked legacy form (no tree picker).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { subAccountsAPI } from '../api/sub-accounts';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { PlacementTreePicker, type PlacementSelection } from '../components/PlacementTreePicker';
import {
  ReadonlyFormField,
  RegistrationActionButtons,
  RegistrationAgreementRow,
  SponsorBalanceGrid,
  StarPackageSelect,
  subPaymentFormula,
  type RegisterStarPackage,
} from '../components/global-players/GlobalTableRegistrationForm';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';

type ReinvestCoin = 'sp1' | 'sp3';

interface OpenSubPreview {
  master: { id: number; username: string; level_code: string };
  referrer?: {
    id: number;
    username: string;
    player_number: string;
    nickname: string;
  };
  next_username: string;
  zone: 'L' | 'R';
  zone_label: string;
  placement: {
    id: number;
    username: string;
    player_number?: string;
    nickname?: string;
  };
  leg_performance: { left: string; right: string };
  star_package: RegisterStarPackage;
  e3_enabled?: boolean;
  available_packages?: RegisterStarPackage[];
  sub_count: number;
  reinvest_coin_type: ReinvestCoin;
  pay: { coin: ReinvestCoin; coin_amount: string; tp_amount: string; fc_amount: string };
  required: { coin: ReinvestCoin; coin_amount: string; tp: string; fc: string };
  balances: { coin: string; tp: string; fc: string };
  sufficient: boolean;
}

function useLockedPlacement(searchParams: URLSearchParams) {
  return useMemo(() => {
    const fromTable = searchParams.get('from') === 'global-table';
    const id = Number(searchParams.get('placement_user_id') || '');
    const pos = (searchParams.get('position') ?? '').toUpperCase();
    if (!fromTable || !Number.isFinite(id) || id <= 0 || (pos !== 'L' && pos !== 'R')) {
      return null;
    }
    return {
      userId: id,
      position: pos as 'L' | 'R',
      playerNumber: searchParams.get('placement_player')?.trim() || String(id),
      nickname: searchParams.get('placement_nickname')?.trim() || '',
    };
  }, [searchParams]);
}

export function OpenSubAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile) as Record<string, unknown> | null;

  const rootUserId = profile?.id as number | undefined;
  const rootUsername = (profile?.username as string | undefined) ?? '—';
  const lockedPlacement = useLockedPlacement(searchParams);

  const urlPlacement = useMemo(() => {
    const id = Number(searchParams.get('placement_user_id') || searchParams.get('parent_id') || '');
    const pos = (searchParams.get('position') ?? searchParams.get('zone') ?? '').toUpperCase();
    const usernameHint = searchParams.get('placement_nickname')?.trim() || searchParams.get('placement_username')?.trim();
    return {
      userId: Number.isFinite(id) && id > 0 ? id : undefined,
      position: pos === 'R' ? ('R' as const) : pos === 'L' ? ('L' as const) : undefined,
      usernameHint: usernameHint || undefined,
    };
  }, [searchParams]);

  const [selection, setSelection] = useState<PlacementSelection | null>(() => {
    if (!lockedPlacement) return null;
    return {
      userId: lockedPlacement.userId,
      username: lockedPlacement.nickname || String(lockedPlacement.userId),
      position: lockedPlacement.position,
    };
  });
  const [starPackage, setStarPackage] = useState<RegisterStarPackage>('E2');
  const [reinvestCoin, setReinvestCoin] = useState<ReinvestCoin>('sp1');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const placementPreviewId = selection?.userId ?? urlPlacement.userId;

  const previewQuery = useQuery({
    queryKey: ['sub-accounts', 'open-preview', placementPreviewId, starPackage, reinvestCoin],
    queryFn: () =>
      subAccountsAPI.openPreview({
        ...(placementPreviewId ? { placement_user_id: placementPreviewId } : {}),
        star_package: starPackage,
        reinvest_coin_type: reinvestCoin,
      }) as Promise<OpenSubPreview>,
    enabled: !!rootUserId,
  });

  const balancesQuery = useQuery({
    queryKey: ['balances', 'open-sub', rootUserId],
    queryFn: () => usersAPI.getBalances(),
    enabled: !!rootUserId && !!lockedPlacement,
    staleTime: 30_000,
  });

  const preview = previewQuery.data;
  const balances = balancesQuery.data?.balances;
  const availablePackages: RegisterStarPackage[] = preview?.available_packages?.length
    ? preview.available_packages
    : ['E1', 'E2'];

  useEffect(() => {
    if (!availablePackages.includes(starPackage)) {
      setStarPackage(availablePackages.includes('E2') ? 'E2' : availablePackages[0]!);
    }
  }, [availablePackages, starPackage]);

  useEffect(() => {
    if (selection || !rootUserId || lockedPlacement) return;
    if (urlPlacement.userId && urlPlacement.position) {
      setSelection({
        userId: urlPlacement.userId,
        username:
          preview?.placement?.username ??
          urlPlacement.usernameHint ??
          (urlPlacement.userId === rootUserId ? rootUsername : String(urlPlacement.userId)),
        position: urlPlacement.position,
      });
      return;
    }
    if (!preview) return;
    setSelection({
      userId: rootUserId,
      username: preview.master.username,
      position: preview.zone,
    });
  }, [preview, rootUserId, rootUsername, selection, lockedPlacement, urlPlacement.userId, urlPlacement.position, urlPlacement.usernameHint]);

  const previewErrorText =
    previewQuery.isError && previewQuery.error instanceof ApiError
      ? previewQuery.error.message
      : previewQuery.isError
        ? t('openSubAccount.previewFailed', '无法加载开通预览，请稍后重试')
        : null;

  const openMut = useMutation({
    mutationFn: () =>
      subAccountsAPI.open({
        reinvest_coin_type: reinvestCoin,
        transaction_password: txPassword,
        security_answer: securityAnswer,
        position: selection!.position,
        placement_user_id: selection!.userId,
        star_package: starPackage,
      }) as Promise<{ username?: string }>,
    onSuccess: (data: { username?: string }) => {
      setMsg({
        type: 'ok',
        text: t('openSubAccount.success', {
          username: data?.username ?? preview?.next_username ?? '—',
          defaultValue: `子账户 ${data?.username ?? ''} 已开通`,
        }),
      });
      void qc.invalidateQueries({ queryKey: ['sub-accounts'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['binary-tree'] });
      setTimeout(() => navigate('/sub-accounts', { replace: true }), 1800);
    },
    onError: (e: unknown) => {
      setMsg({
        type: 'err',
        text: e instanceof ApiError ? e.message : t('openSubAccount.failed', 'Failed to open sub-account'),
      });
    },
  });

  const handleSubmit = () => {
    setMsg(null);
    if (!agreed && lockedPlacement) {
      setMsg({ type: 'err', text: t('auth.agreeTerms', '请先同意用户协议') });
      return;
    }
    if (!selection) {
      setMsg({ type: 'err', text: t('registerMember.pickPlacement', '请先在网体上选择安置位置') });
      return;
    }
    if (!preview?.sufficient) {
      setMsg({ type: 'err', text: t('openSubAccount.insufficient', '支付币种 / TP / FC 余额不足') });
      return;
    }
    openMut.mutate();
  };

  const payCoin = (preview?.required.coin ?? reinvestCoin).toUpperCase();
  const requiredCoin = preview?.required.coin_amount ?? '—';
  const requiredTp = preview?.required.tp ?? '0';
  const requiredFc = preview?.required.fc ?? '0';
  const balanceCoin =
    preview?.balances.coin ?? (balances as Record<string, string> | undefined)?.[reinvestCoin] ?? '0';
  const balanceTp = preview?.balances.tp ?? balances?.tp ?? '0';
  const balanceFc = preview?.balances.fc ?? (balances as { fc?: string })?.fc ?? '0';
  const hasTpLeg = Number(requiredTp) > 0;
  const payLabel = hasTpLeg
    ? `支付 ${requiredCoin} ${payCoin} + ${requiredTp} TP + ${requiredFc} FC 并开子账户`
    : `支付 ${requiredCoin} ${payCoin} + ${requiredFc} FC 并开子账户`;

  const securityQuestion = profile?.security_question as string | undefined;
  const questionText = securityQuestion ?? t('convert.defaultSecurityQuestion');

  const nextUsername = preview?.next_username ?? '—';
  const referrerPlayerNumber =
    preview?.referrer?.player_number ??
    (profile?.player_number as string | undefined)?.trim() ??
    String(rootUserId ?? '—');
  const referrerNickname =
    preview?.referrer?.nickname ??
    (profile?.nickname as string | undefined)?.trim() ??
    rootUsername;
  const placementPlayerNumber =
    preview?.placement?.player_number ??
    (selection ? String(selection.userId) : '—');
  const placementNickname =
    preview?.placement?.nickname ?? selection?.username ?? '—';

  const fmtPerf = (raw: string | undefined) => {
    const n = Number(raw ?? '0');
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };
  const subNickname = useMemo(() => {
    const base = (profile?.nickname as string | undefined)?.trim() || rootUsername;
    const suffix = nextUsername.includes('-') ? nextUsername.split('-').pop() : nextUsername;
    return suffix ? `${base}-${suffix}` : base;
  }, [nextUsername, profile?.nickname, rootUsername]);

  if (lockedPlacement) {
    const placementId = lockedPlacement.playerNumber;
    const placementNick = lockedPlacement.nickname;
    const canSubmit =
      !!selection &&
      !!txPassword.trim() &&
      agreed &&
      !!preview?.sufficient &&
      !openMut.isPending &&
      !previewQuery.isLoading;

    return (
      <div className="relative min-h-full" style={{ background: C.pageBg }}>
        <div className="relative mx-auto min-h-full w-full max-w-[430px]">
          <ThemedPageHeader
            title={t('globalPlayers.registerSubAccount', '注册子账户')}
            onBack={() => navigate(-1)}
            theme={C}
          />

          <main className="space-y-4 px-4 pb-8 pt-[70px]">
          {previewErrorText ? <ThemedNotice tone="error">{previewErrorText}</ThemedNotice> : null}

          <ThemedPanel className="space-y-4 p-4">
            <ReadonlyFormField
              label={t('registerForm.referrerId', '推荐人ID')}
              value={referrerPlayerNumber}
              hint={`${t('registerForm.referrerNickname', '推荐人昵称')}:${referrerNickname}`}
            />
            <ReadonlyFormField
              label={t('registerForm.placementId', '接点人ID')}
              value={placementId}
              hint={
                placementNick
                  ? `${t('registerForm.placementNickname', '接点人昵称')}:${placementNick}`
                  : undefined
              }
            />
            <ReadonlyFormField
              label={t('registerForm.loginName', '玩家登录名')}
              value={nextUsername}
            />
            <ReadonlyFormField
              label={t('registerForm.nickname', '昵称')}
              value={subNickname}
            />
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                <div style={{ color: C.muted }}>{t('openSubAccount.leftPerf', '左区业绩')}</div>
                <div className="mt-1 font-mono" style={{ color: C.text }}>
                  {fmtPerf(preview?.leg_performance.left)}
                </div>
              </div>
              <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                <div style={{ color: C.muted }}>{t('openSubAccount.rightPerf', '右区业绩')}</div>
                <div className="mt-1 font-mono" style={{ color: C.text }}>
                  {fmtPerf(preview?.leg_performance.right)}
                </div>
              </div>
            </div>

            <SponsorBalanceGrid
              cells={[
                { key: 'rp', label: t('registerForm.rpBalance', 'RP余额'), value: balances?.rp ?? '0', color: '#F5EC63' },
                { key: 'tp', label: t('registerForm.tpBalance', 'TP余额'), value: balances?.tp ?? '0', color: '#F5EC63' },
                { key: 'fc', label: t('registerForm.fcBalance', 'FC余额'), value: (balances as { fc?: string })?.fc ?? '0', color: '#F5EC63' },
                { key: 'sp1', label: t('registerForm.sp1Balance', 'SP1余额'), value: (balances as { sp1?: string })?.sp1 ?? '0', color: '#58d6ff' },
                { key: 'sp2', label: t('registerForm.sp2Balance', 'SP2余额'), value: (balances as { sp2?: string })?.sp2 ?? '0', color: '#ADD741' },
              ]}
            />

            <StarPackageSelect
              label={t('registerForm.starLevel', '玩家星级')}
              value={starPackage}
              available={availablePackages}
              onChange={setStarPackage}
            />

            <ThemedField label={t('registerForm.paymentMethod', '支付方式')}>
              <div className="grid grid-cols-2 gap-2">
                {(['sp1', 'sp3'] as const).map((coin) => {
                  const active = reinvestCoin === coin;
                  return (
                    <button
                      key={coin}
                      type="button"
                      onClick={() => setReinvestCoin(coin)}
                      className="rounded-xl border px-2 py-2.5 text-xs font-semibold uppercase transition active:scale-[0.98]"
                      style={{
                        borderColor: active ? C.gold : C.line,
                        color: active ? C.gold : C.muted,
                        background: active ? 'rgba(212,175,55,0.12)' : 'transparent',
                      }}
                    >
                      {coin.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px]" style={{ color: C.muted }}>
                {subPaymentFormula(reinvestCoin)}
              </p>
            </ThemedField>

            <TransactionSecurity
              txPassword={txPassword}
              onTxPasswordChange={setTxPassword}
              showSecurity={showSecurity}
              onShowSecurityChange={setShowSecurity}
              securityAnswer={securityAnswer}
              onSecurityAnswerChange={setSecurityAnswer}
              securityQuestionText={questionText}
            />

            <div className="flex items-end justify-between gap-3">
              <p className="text-[11px]" style={{ color: C.muted }}>
                {t('registerForm.subTxPasswordHint', '初始交易密码同主账户')}
              </p>
              <RegistrationAgreementRow agreed={agreed} onAgreedChange={setAgreed} />
            </div>
          </ThemedPanel>

          {msg ? <ThemedNotice tone={msg.type === 'ok' ? 'success' : 'error'}>{msg.text}</ThemedNotice> : null}

          <RegistrationActionButtons
            onCancel={() => navigate(-1)}
            onSubmit={handleSubmit}
            submitLabel={t('common.submit', '提交')}
            submitting={openMut.isPending}
            submitDisabled={!canSubmit}
          />
        </main>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
      <ThemedPageHeader title={t('openSubAccount.title', 'Open Sub-account')} onBack={() => navigate(-1)} theme={C} />

      <main className="space-y-4 px-4 pb-28 pt-[70px]">
        {previewErrorText && <ThemedNotice tone="error">{previewErrorText}</ThemedNotice>}

        <ThemedPanel className="p-4">
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            {t(
              'openSubAccount.subtitle',
              '选择支付币种在主号下创建子账户（SP1 按 95%+5%TP，SP3 按 90%+10%TP，另收 0.05 FC）；从全球玩家表点空位，或在网体上选择左/右区安置。',
            )}
          </p>
        </ThemedPanel>

        {rootUserId ? (
          <ThemedPanel className="p-4">
            <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
              {t('openSubAccount.placementTitle', '安置与账号')}
            </div>
            {preview && (
              <div className="mb-4 space-y-3">
                <ReadonlyFormField
                  label={t('registerForm.referrerId', '推荐人ID')}
                  value={referrerPlayerNumber}
                  hint={`${t('registerForm.referrerNickname', '推荐人昵称')}:${referrerNickname}`}
                />
                <ReadonlyFormField
                  label={t('registerForm.placementId', '接点人ID')}
                  value={placementPlayerNumber}
                  hint={`${t('registerForm.placementNickname', '接点人昵称')}:${placementNickname}`}
                />
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.nextUsername', 'Sub username')}</div>
                    <div className="mt-1 font-mono font-semibold" style={{ color: C.gold }}>
                      {preview.next_username}
                    </div>
                  </div>
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.bigLeg', '大区')}</div>
                    <div className="mt-1 font-semibold" style={{ color: C.text }}>
                      {preview.zone_label} ({preview.zone})
                    </div>
                  </div>
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.leftPerf', '左区业绩')}</div>
                    <div className="mt-1 font-mono" style={{ color: C.text }}>
                      {fmtPerf(preview.leg_performance.left)}
                    </div>
                  </div>
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.rightPerf', '右区业绩')}</div>
                    <div className="mt-1 font-mono" style={{ color: C.text }}>
                      {fmtPerf(preview.leg_performance.right)}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <PlacementTreePicker
              rootUserId={rootUserId}
              rootUsername={rootUsername}
              selection={selection}
              onSelect={setSelection}
              initialUserId={urlPlacement.userId ?? rootUserId}
              initialPosition={urlPlacement.position ?? preview?.zone}
            />
          </ThemedPanel>
        ) : null}

        {preview && (
          <ThemedPanel className="p-4">
            <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
              {t('openSubAccount.costTitle', '开通费用')}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                <div style={{ color: C.muted }}>{payCoin}</div>
                <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                  {requiredCoin}
                </div>
                <div className="mt-1" style={{ color: C.muted }}>
                  {t('openSubAccount.balance', '余额')}: {Number(balanceCoin).toFixed(2)}
                </div>
              </div>
              {hasTpLeg && (
                <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                  <div style={{ color: C.muted }}>TP</div>
                  <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                    {requiredTp}
                  </div>
                  <div className="mt-1" style={{ color: C.muted }}>
                    {t('openSubAccount.balance', '余额')}: {Number(balanceTp).toFixed(2)}
                  </div>
                </div>
              )}
              <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                <div style={{ color: C.muted }}>FC</div>
                <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                  {requiredFc}
                </div>
                <div className="mt-1" style={{ color: C.muted }}>
                  {t('openSubAccount.balance', '余额')}: {Number(balanceFc).toFixed(2)}
                </div>
              </div>
            </div>
            {!preview.sufficient && (
              <ThemedNotice tone="error">
                {t('openSubAccount.insufficient', '支付币种 / TP / FC 余额不足')}
              </ThemedNotice>
            )}
          </ThemedPanel>
        )}

        <ThemedPanel className="space-y-4 p-4">
          <StarPackageSelect
            label={t('registerForm.starLevel', '玩家星级')}
            value={starPackage}
            available={availablePackages}
            onChange={setStarPackage}
          />

          <div>
            <div className="mb-2 text-xs font-medium" style={{ color: C.muted }}>
              {t('openSubAccount.reinvestType', 'Reinvest coin type')} <span className="text-negative">*</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(['sp1', 'sp3'] as const).map((coin) => {
                const active = reinvestCoin === coin;
                return (
                  <button
                    key={coin}
                    type="button"
                    onClick={() => setReinvestCoin(coin)}
                    className="rounded-xl border px-2 py-3 text-sm font-semibold uppercase transition active:scale-[0.98]"
                    style={{
                      borderColor: active ? `${C.gold}88` : C.line,
                      background: active ? `${C.gold}14` : C.inputBg,
                      color: active ? C.gold : C.muted,
                    }}
                  >
                    {coin.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px]" style={{ color: C.muted }}>
              {subPaymentFormula(reinvestCoin)}
            </p>
          </div>

          <p className="text-[11px] leading-relaxed" style={{ color: C.muted }}>
            {t('openSubAccount.noSubLoginPassword')}
          </p>

          <TransactionSecurity
            txPassword={txPassword}
            onTxPasswordChange={setTxPassword}
            showSecurity={showSecurity}
            onShowSecurityChange={setShowSecurity}
            securityAnswer={securityAnswer}
            onSecurityAnswerChange={setSecurityAnswer}
            securityQuestionText={questionText}
          />
        </ThemedPanel>

        {msg && <ThemedNotice tone={msg.type === 'ok' ? 'success' : 'error'}>{msg.text}</ThemedNotice>}
      </main>

      <ThemedBottomActionBar>
        <ThemedSubmitButton
          onClick={handleSubmit}
          disabled={
            openMut.isPending ||
            !selection ||
            !txPassword.trim() ||
            previewQuery.isLoading ||
            previewQuery.isError ||
            !preview ||
            !preview.sufficient
          }
        >
          {openMut.isPending ? t('auth.loggingIn', 'Processing…') : payLabel}
        </ThemedSubmitButton>
      </ThemedBottomActionBar>
      </div>
    </div>
  );
}
